/**
 * Sprinkler Tool
 * 
 * Places fire sprinklers with hydraulic parameters.
 */

import { v4 as uuidv4 } from 'uuid';
import { SprinklerElement, SprinklerGeometryData } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';

export class SprinklerTool extends BaseTool {
    id = 'sprinkler';
    name = 'Sprinkler';
    cursor = 'crosshair';

    private currentPoint: THREE.Vector3 | null = null;
    private currentNormal: THREE.Vector3 = new THREE.Vector3(0, 0, 1);
    private currentHostId: string | null = null;

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        if (this.currentPoint && this.currentHostId) {
            this.createSprinkler();
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;

        // Find host (Ceiling usually) - reusing HostManager logic pattern if available
        const result = this.hostManager?.findHost(event.intersections, {
            categories: ['Ceilings', 'Floors']
        });

        if (result) {
            this.currentPoint = result.point;
            this.currentNormal = result.normal;
            this.currentHostId = result.elementId;
        } else {
            // Fallback to ground
            const point = this.getIntersection(event);
            if (point) {
                this.currentPoint = this.snapToGrid(point);
                this.currentNormal = new THREE.Vector3(0, 0, 1);
                this.currentHostId = 'ground-plane';
            } else {
                this.currentPoint = null;
            }
        }

        this.updatePreview();
    }

    onPointerUp(_event: ThreeEvent<PointerEvent>): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            this.deactivate();
        }
    }

    private updatePreview() {
        if (!this.context) return;

        if (!this.currentPoint) {
            this.context.appStore.setToolPreview(null);
            return;
        }

        this.context.appStore.setToolPreview({
            type: 'sphere',
            position: this.currentPoint,
            radius: 0.05,
            color: '#ff0000' // Red for fire
        });
    }

    private createSprinkler() {
        if (!this.context || !this.currentPoint || !this.currentHostId) return;

        const geometryData: SprinklerGeometryData = {
            position: [this.currentPoint.x, this.currentPoint.y, this.currentPoint.z],
            faceNormal: [this.currentNormal.x, this.currentNormal.y, this.currentNormal.z],
            radius: 0.05
        };

        const density = 0.1; // gpm/sqft or mm/min, let's say gpm/sqft for simplicity
        const coverage = 130; // sqft
        const kFactor = 5.6;

        // Q = Density * Area (simplified)
        const flow = density * coverage;

        // P = (Q/K)^2
        const pressure = Math.pow(flow / kFactor, 2);

        const sprinkler: SprinklerElement = {
            id: uuidv4(),
            category: 'Sprinklers',
            type: 'Pendant - K5.6',
            parameters: {
                'K-Factor': `${kFactor}`,
                'Coverage': `${coverage} sqft`,
                'Density': `${density} gpm/sqft`,
                'Flow': `${flow.toFixed(1)} gpm`,
                'Pressure': `${pressure.toFixed(1)} psi`,
                'System Type': 'Fire Protection Wet'
            },
            constraints: [
                { type: 'HOST_CONTAINED', targetId: this.currentHostId, data: {} }
            ],
            geometryData
        };

        this.context.bimStore.addElement(sprinkler);
    }
}
